import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { ArticleMasthead } from "@/components/blog/article-masthead";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { useSEO, useSEOSettings, generateBreadcrumbSchema } from "@/lib/seo-utils";

/**
 * Human-readable sitemap, and a live crawl path.
 *
 * Two things make it a crawl path rather than a decorative page:
 *
 *   1. It renders server-side. The links are real anchors present in the HTML
 *      response, so a crawler that executes no JavaScript still follows them.
 *      A client-only list is invisible to exactly the visitors this page exists
 *      for.
 *   2. It is built from the same live query as sitemap.xml. Publish a guide and
 *      it appears here with no further action. A hardcoded list of links stops
 *      being a path the first time somebody forgets to update it.
 *
 * It is also the reason deep pages are not orphaned: every published guide is
 * one hop from here, and this page is one hop from every page's footer.
 */

interface SitemapSection {
  title: string;
  links: { href: string; label: string }[];
}

export default function SitemapPage() {
  const { seoSettings } = useSEOSettings();
  const { data } = useQuery<{ sections: SitemapSection[]; counts: Record<string, number> }>({
    queryKey: ["/api/sitemap-index"],
  });

  const seoHead = useSEO({
    pageType: "sitemap",
    title: "Sitemap | The Kualoa Beach House, Oahu",
    description:
      "Every page on vacationrentaloahu.co: the house, booking, house rules, the Oahu travel guide and nearby attractions.",
    canonical: "https://vacationrentaloahu.co/sitemap",
    ogType: "website",
    structuredData: seoSettings
      ? [generateBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Sitemap", url: "/sitemap" }])]
      : undefined,
  });

  const sections = data?.sections ?? [];

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main id="main-content">
          <ArticleMasthead
            eyebrow="Everything on this site"
            title="Sitemap"
            deck="Every page we publish, in one place: the house, planning your trip, and all of the Oahu travel guides."
          />

          <div className="container mx-auto max-w-5xl px-6">
            <Breadcrumbs
              items={[{ name: "Home", url: "/" }, { name: "Sitemap", url: "/sitemap" }]}
              className="py-8"
            />
          </div>

          <section className="container mx-auto max-w-5xl px-6 pb-20">
            {sections.length === 0 ? (
              <p className="font-sans text-[15px] text-ink-soft">Loading the site index…</p>
            ) : (
              <div className="space-y-14">
                {sections.map((section) => (
                  <div key={section.title}>
                    <h2 className="mb-6 border-b border-sand-deep pb-4 font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
                      {section.title}
                    </h2>
                    <ul className="grid gap-x-10 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                      {section.links.map((link) => (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            className="flex min-h-[44px] items-center border-b border-transparent py-2 font-sans text-[15px] leading-snug text-ink-soft transition-colors hover:border-sand-deep hover:text-forest"
                            data-testid={`sitemap-link-${link.href}`}
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-16 border-t border-sand-deep pt-8 font-sans text-[13px] leading-relaxed text-driftwood">
              This page is generated from the site itself, so it is never out of date. Machines
              may prefer{" "}
              <a href="/sitemap.xml" className="border-b border-sand-deep text-forest">sitemap.xml</a>.
            </p>
          </section>
        </main>
        <StickyMobileBookCta />
        <Footer />
      </div>
    </>
  );
}
